import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiDownload, FiX } from "react-icons/fi";
import {
  downloadAttachment,
  useMediaUrl,
} from "../../hooks/comms/useMediaUrl";
import {
  ATTACHMENT_KINDS,
  attachmentKindLabel,
} from "../../utils/commsAttachments";

/**
 * Full-screen media viewer for chat attachments. Escape / scrim-click close,
 * arrow keys page between items, and focus returns to the trigger on close
 * (same interaction contract as Drawer).
 *
 * @param {Object} props
 * @param {import('../../utils/commsAttachments').ChatAttachment[]} props.items
 * @param {number} props.index - Index of the item on screen.
 * @param {(next: number) => void} props.onIndexChange
 * @param {() => void} props.onClose
 */
export const MediaLightbox = ({ items, index, onIndexChange, onClose }) => {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const previouslyFocused = useRef(null);

  const count = items?.length || 0;
  const current = count ? items[Math.min(Math.max(index, 0), count - 1)] : null;

  const step = useCallback(
    (delta) => {
      if (count < 2) return;
      onIndexChange((index + delta + count) % count);
    },
    [count, index, onIndexChange]
  );

  // Escape to close, arrows to page, and keep Tab inside the dialog.
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
      if (event.key === "Tab") {
        const focusable = panelRef.current?.querySelectorAll(
          'button, a[href], video, audio, iframe, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || !focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [onClose, step]);

  // Move focus in on open and hand it back to the trigger on close.
  useEffect(() => {
    previouslyFocused.current = document.activeElement;
    closeRef.current?.focus();
    const trigger = previouslyFocused.current;
    return () => {
      if (trigger instanceof HTMLElement && document.contains(trigger)) {
        trigger.focus();
      }
    };
  }, []);

  if (!current) return null;

  const title = current.fileName || attachmentKindLabel(current.kind);

  return (
    <div
      className="fixed inset-0 z-[120] flex flex-col bg-black/80 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex h-full w-full flex-col"
      >
        <div className="flex flex-shrink-0 items-center justify-between gap-4 px-1 py-2">
          <h2 className="min-w-0 truncate text-sm font-semibold text-white">
            {title}
            {count > 1 && (
              <span className="ml-2 font-normal text-white/60">
                {index + 1} of {count}
              </span>
            )}
          </h2>
          <div className="flex flex-shrink-0 items-center gap-2">
            <DownloadButton attachment={current} />
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close viewer"
              className="rounded-lg bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        <div className="relative flex min-h-0 flex-1 items-center justify-center">
          {count > 1 && (
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Previous attachment"
              className="absolute left-0 z-10 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
            >
              <FiChevronLeft size={24} />
            </button>
          )}

          <LightboxMedia attachment={current} />

          {count > 1 && (
            <button
              type="button"
              onClick={() => step(1)}
              aria-label="Next attachment"
              className="absolute right-0 z-10 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
            >
              <FiChevronRight size={24} />
            </button>
          )}
        </div>

        {current.caption && (
          <p className="flex-shrink-0 px-1 py-2 text-center text-sm text-white/80">
            {current.caption}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Saves the open attachment. Goes via a blob so the original filename is
 * preserved — a cross-origin `download` attribute would be ignored.
 *
 * @param {{ attachment: import('../../utils/commsAttachments').ChatAttachment }} props
 */
const DownloadButton = ({ attachment }) => {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    try {
      await downloadAttachment(attachment);
    } catch (err) {
      // The viewer already surfaces load failures; nothing to add here.
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      aria-label="Download"
      className="rounded-lg bg-white/10 p-2 text-white transition-colors hover:bg-white/20 disabled:opacity-50"
    >
      <FiDownload size={18} />
    </button>
  );
};

/**
 * The media element itself, chosen by attachment kind. The signed URL streams
 * directly from storage, so a large video seeks and plays without buffering
 * the whole file first.
 *
 * @param {{ attachment: import('../../utils/commsAttachments').ChatAttachment }} props
 */
const LightboxMedia = ({ attachment }) => {
  const { kind, fileName, caption } = attachment;
  const { url, loading, error, refresh } = useMediaUrl(attachment);

  if (loading) {
    return (
      <p className="text-sm text-white/70" role="status">
        Loading {attachmentKindLabel(kind)}…
      </p>
    );
  }

  if (!url) {
    return (
      <p className="text-sm text-white/70">
        {error?.message || "This attachment is unavailable."}
      </p>
    );
  }

  if (kind === ATTACHMENT_KINDS.IMAGE) {
    return (
      <img
        src={url}
        alt={caption || fileName || "Attachment"}
        onError={refresh}
        className="max-h-full max-w-full object-contain"
      />
    );
  }

  if (kind === ATTACHMENT_KINDS.VIDEO) {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        key={url}
        src={url}
        controls
        autoPlay
        aria-label={caption || fileName || "Video attachment"}
        onError={refresh}
        className="max-h-full max-w-full"
      />
    );
  }

  if (kind === ATTACHMENT_KINDS.VOICE) {
    return (
      <div className="w-full max-w-md rounded-xl bg-white/10 p-6">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio
          key={url}
          src={url}
          controls
          autoPlay
          aria-label={caption || fileName || "Voice note"}
          onError={refresh}
          className="w-full"
        />
      </div>
    );
  }

  if (kind === ATTACHMENT_KINDS.PDF) {
    return (
      <iframe
        src={url}
        title={fileName || "PDF attachment"}
        className="h-full w-full rounded-lg bg-white"
      />
    );
  }

  return (
    <p className="text-sm text-white/70">
      {fileName || "This file"} can't be previewed — use Download.
    </p>
  );
};

export default MediaLightbox;
